/**
 * OverSkill Platform SDK
 *
 * Runtime utilities for OverSkill generated apps.
 * This SDK is loaded from CDN and provides hot-updatable platform features.
 *
 * Usage:
 * <script src="https://sdk.overskill.com/v1/platform.js" data-app-id="YOUR_APP_ID"></script>
 *
 * Or via config:
 * <script>
 *   window.APP_CONFIG = { APP_ID: 'YOUR_APP_ID', ENVIRONMENT: 'production' };
 * </script>
 * <script src="https://sdk.overskill.com/v1/platform.js"></script>
 */

import { ConsoleLogger } from './console-logger';
import { ErrorReporter } from './error-reporter';
import { NetworkLogger } from './network-logger';
import { PushRecovery } from './push-recovery';
import { installPushScopeGuard } from './push-sw-scope';
import type { PlatformConfig, OverSkillPlatformInstance } from './types';

// SDK Version - updated on release
export const VERSION = '1.0.16';

/**
 * Extract appId from preview URL as fallback
 * Supports multiple preview URL formats:
 * - preview-{appId}.overskill.app
 * - {appId}.overskill.app
 * - preview-{appId}-{uuid}-{random}.sprites.app (Cloudflare Workers for Platforms)
 * - {appId}.sprites.app
 */
function extractAppIdFromUrl(): string | null {
  try {
    const hostname = window.location.hostname;

    // overskill.app format: preview-{appId}.overskill.app or {appId}.overskill.app
    if (hostname.includes('overskill.app')) {
      const subdomain = hostname.split('.')[0];
      if (subdomain.startsWith('preview-')) {
        return subdomain.replace('preview-', '');
      }
      return subdomain;
    }

    // sprites.app format: preview-{appId}-{uuid}-{random}.sprites.app
    // Also handles overskillprojects.com proxy (Feb 2026): preview-{appId}-{uuid}-{random}.overskillprojects.com
    // The appId is the second segment after "preview-"
    if (hostname.includes('sprites.app') || hostname.includes('overskillprojects.com')) {
      const subdomain = hostname.split('.')[0];
      if (subdomain.startsWith('preview-')) {
        // Format: preview-{appId}-{rest...}
        // Example: preview-bawykxg-8a842939-bb2hd → extract "bawykxg"
        const parts = subdomain.split('-');
        if (parts.length >= 2) {
          return parts[1]; // The appId is right after "preview"
        }
      }
      // Direct app URL on sprites.app
      return subdomain;
    }
  } catch {
    // Fallthrough to null
  }
  return null;
}

/**
 * Find our script tag, even for defer/async scripts where document.currentScript is null
 */
function findSdkScriptTag(): HTMLScriptElement | null {
  // document.currentScript works for synchronous scripts
  if (document.currentScript) {
    return document.currentScript as HTMLScriptElement;
  }

  // For defer/async scripts, find by SDK URL pattern
  const scripts = document.querySelectorAll('script[src*="sdk.overskill.com"], script[src*="platform.js"]');
  for (const script of scripts) {
    if (script instanceof HTMLScriptElement) {
      return script;
    }
  }

  return null;
}

/**
 * Get configuration from various sources
 */
function getConfig(): PlatformConfig {
  // Priority 1: data attributes on script tag
  // NOTE: document.currentScript is null for defer/async scripts, so we search by URL
  const scriptTag = findSdkScriptTag();
  const dataAppId = scriptTag?.getAttribute('data-app-id');
  const dataEnvironment = scriptTag?.getAttribute('data-environment');
  const dataBridgeUrl = scriptTag?.getAttribute('data-bridge-url');
  const dataDebug = scriptTag?.getAttribute('data-debug');
  const dataPushRecovery = scriptTag?.getAttribute('data-push-recovery');
  const dataPushScopeGuard = scriptTag?.getAttribute('data-push-scope-guard');

  // Priority 2: window.APP_CONFIG
  const appConfig = window.APP_CONFIG || {};

  // Priority 3: import.meta.env (Vite) - accessed via window if set
  const viteEnv = (window as unknown as Record<string, unknown>).__VITE_ENV__ as Record<string, string> | undefined;

  // Priority 4: Extract from preview URL (fallback for older apps without APP_CONFIG)
  const urlAppId = extractAppIdFromUrl();

  return {
    appId:
      dataAppId ||
      appConfig.APP_ID ||
      viteEnv?.VITE_APP_ID ||
      urlAppId ||
      'unknown',
    environment:
      dataEnvironment ||
      appConfig.ENVIRONMENT ||
      viteEnv?.VITE_ENVIRONMENT ||
      'production',
    bridgeUrl: dataBridgeUrl || appConfig.BRIDGE_URL,
    debug: dataDebug === 'true' || appConfig.ENVIRONMENT === 'development',
    // Enabled by default; opt-out via data-push-recovery="false" or
    // APP_CONFIG.PUSH_RECOVERY_ENABLED = false.
    pushRecovery: dataPushRecovery === 'false' || appConfig.PUSH_RECOVERY_ENABLED === false ? false : true,
    // Enabled by default; opt-out via data-push-scope-guard="false" or
    // APP_CONFIG.PUSH_SCOPE_GUARD_ENABLED = false. Separate from pushRecovery
    // on purpose: one is a banner, the other wraps serviceWorker.register for
    // the whole fleet and needs its own kill switch (#3705).
    pushScopeGuard:
      dataPushScopeGuard === 'false' || appConfig.PUSH_SCOPE_GUARD_ENABLED === false ? false : true,
  };
}

/**
 * Disable deprecated bundled consoleLogger if it exists
 * Apps built before Jan 2026 may have the old bundled version that makes
 * requests without www normalization, causing CORS redirect errors.
 */
function disableLegacyConsoleLogger(): void {
  // Check if old bundled consoleLogger exists
  const legacyLogger = (window as unknown as Record<string, unknown>).consoleLogger as {
    intervalId?: ReturnType<typeof setInterval>;
    originalConsole?: Record<string, (...args: unknown[]) => void>;
    logs?: unknown[];
  } | undefined;

  if (!legacyLogger || typeof legacyLogger !== 'object') {
    return; // No legacy logger to disable
  }

  try {
    // Stop periodic sending
    if (legacyLogger.intervalId) {
      clearInterval(legacyLogger.intervalId);
    }

    // Restore original console methods if the old logger captured them
    // This prevents the chain: Original -> LegacyLogger -> SDK
    // We want: Original -> SDK (only)
    if (legacyLogger.originalConsole) {
      if (legacyLogger.originalConsole.log) console.log = legacyLogger.originalConsole.log;
      if (legacyLogger.originalConsole.error) console.error = legacyLogger.originalConsole.error;
      if (legacyLogger.originalConsole.warn) console.warn = legacyLogger.originalConsole.warn;
      if (legacyLogger.originalConsole.info) console.info = legacyLogger.originalConsole.info;
      if (legacyLogger.originalConsole.debug) console.debug = legacyLogger.originalConsole.debug;
    }

    // Clear any pending logs
    if (legacyLogger.logs) {
      legacyLogger.logs = [];
    }

    // Remove from window to prevent future access
    delete (window as unknown as Record<string, unknown>).consoleLogger;

    console.log('[OverSkill Platform SDK] Disabled deprecated bundled consoleLogger');
  } catch (e) {
    // Silently fail - don't block SDK initialization
    console.warn('[OverSkill Platform SDK] Failed to disable legacy consoleLogger:', e);
  }
}

/**
 * Initialize the SDK
 */
function initialize(): OverSkillPlatformInstance {
  const config = getConfig();

  // CRITICAL: Disable legacy bundled consoleLogger FIRST
  // This prevents duplicate requests and CORS errors from old apps
  disableLegacyConsoleLogger();

  // Install the push service-worker scope guard SYNCHRONOUSLY, before any app
  // code runs (#3705 / #4770 / #4200). platform.js is a `defer` script in
  // <head> and the app bundle is a module script at the end of <body> — both
  // land in the document's "execute when parsing has finished" list, in
  // document order, so this wrapper is in place before the app's bundled
  // overskill-sdk can call register('/sw-push.js', { scope: '/' }) and clobber
  // the push-capable worker that owns "/". This is the only lever that reaches
  // an already-deployed app: the offending register() call lives in a
  // lockfile-pinned npm dependency.
  installPushScopeGuard({ enabled: config.pushScopeGuard !== false });

  // Create instances
  const consoleLogger = new ConsoleLogger(config);
  const errorReporter = new ErrorReporter(config);
  const networkLogger = new NetworkLogger(config);
  const pushRecovery = new PushRecovery(config);

  const platform: OverSkillPlatformInstance = {
    version: VERSION,
    consoleLogger,
    errorReporter,
    networkLogger,
    pushRecovery,
    config,
    initialized: true,
  };

  // Log initialization (if debug mode or in iframe)
  const isInIframe = typeof window !== 'undefined' && window.parent !== window;
  if (config.debug || isInIframe) {
    console.log(
      `%c[OverSkill Platform SDK] v${VERSION} loaded`,
      'color: #22c55e; font-weight: bold;',
      {
        appId: config.appId,
        environment: config.environment,
        consoleLoggerEnabled: consoleLogger.isEnabled(),
        errorReporterEnabled: errorReporter.isEnabled(),
        networkLoggerEnabled: networkLogger.isEnabled(),
        pushRecoveryEnabled: pushRecovery.isEnabled(),
      }
    );
  }

  return platform;
}

// Auto-initialize when script loads (IIFE pattern)
let platform: OverSkillPlatformInstance;

if (typeof window !== 'undefined') {
  // Check if already initialized (prevent double-init)
  if (!window.OverSkillPlatform) {
    platform = initialize();
    window.OverSkillPlatform = platform;
  } else {
    platform = window.OverSkillPlatform;
    console.warn('[OverSkill Platform SDK] Already initialized, skipping re-initialization');
  }
}

// Export for ESM usage
export { platform as OverSkillPlatform };
export { ConsoleLogger } from './console-logger';
export { ErrorReporter } from './error-reporter';
export { NetworkLogger } from './network-logger';
export { PushRecovery } from './push-recovery';
export {
  installPushScopeGuard,
  resolveRootPushRegistration,
  pingPushCapability,
  targetsRootPushWorker,
} from './push-sw-scope';
export type * from './types';

// Default export for convenience
export default platform!;
